import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';

// Styled wrappers
const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f8f9fa',
  minHeight: '100vh'
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3)
}));

const Card = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  background: '#fff',
  border: '1px solid #eef2f6',
}));

const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  padding: theme.spacing(1, 0)
}));

const ValueText = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  textAlign: 'right'
}));

const PaymentProfile = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();

  // Mock data; replace with API data when available
  const [data, setData] = useState(null);

  useEffect(() => {
    const mock = {
      startingDate: '02-02-2025',
      endingDate: '02-02-2025',
      gstNumber: '599889989',
      gstAddress:
        'Office Floor, Bestech, Sector 66, Sahibzada Ajit Singh Nagar, Punjab 160066',
      packageCost: '₹5000',
      coupon: 'N/A',
      paymentMode: 'Online',
      autoPayMode: 'UPI',
      totalPayment: '₹5000',
      advance: '₹5000',
      pending: '₹0',
      gst: '₹5000',
      afterGst: '₹5000',
      topUpAmount: '₹5000',
      afterTopup: '₹5000',
      mode: 'NEFT',
      saleCreditMonth: '₹5000',
      paymentDate: '0001-01-01 to 0:00:00'
    };
    setData(mock);
  }, [paymentId]);

  const [sendOpen, setSendOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  if (!data) return null;

  return (
    <PageContainer>
      <Header>
        <Box>
          <Breadcrumbs sx={{ mb: 1 }}>
            <Link color="inherit" onClick={() => navigate('/admin-dashboard')} sx={{ cursor: 'pointer' }}>
              Dashboard
            </Link>
            <Link color="inherit" onClick={() => navigate(-1)} sx={{ cursor: 'pointer' }}>
              Client Profile
            </Link>
            <Typography color="text.primary">Payment Profile</Typography>
          </Breadcrumbs>
          <Typography variant="h4" fontWeight={700}>Payment Profile</Typography>
          {paymentId && (
            <Typography variant="body2" color="text.secondary">Payment ID: {paymentId}</Typography>
          )}
        </Box>
      </Header>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <Row>
              <Typography variant="body2">Starting Date :</Typography>
              <ValueText>{data.startingDate}</ValueText>
            </Row>
            <Row>
              <Typography variant="body2">Ending Date :</Typography>
              <ValueText>{data.endingDate}</ValueText>
            </Row>
            <Row>
              <Typography variant="body2">GST Number :</Typography>
              <ValueText>{data.gstNumber}</ValueText>
            </Row>
            <Row>
              <Typography variant="body2">GST Address :</Typography>
              <ValueText sx={{ maxWidth: 360 }}>{data.gstAddress}</ValueText>
            </Row>
            <Row>
              <Typography variant="body2">Package Cost :</Typography>
              <ValueText>{data.packageCost}</ValueText>
            </Row>
            <Row>
              <Typography variant="body2">Coupon :</Typography>
              <Typography color="text.secondary" fontWeight={600}>N/A</Typography>
            </Row>
            <Row>
              <Typography variant="body2">Payment Mode :</Typography>
              <ValueText>{data.paymentMode}</ValueText>
            </Row>
            <Row>
              <Typography variant="body2">Auto-Pay Mode :</Typography>
              <ValueText>{data.autoPayMode}</ValueText>
            </Row>
          </Card>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              onClick={() => setSendOpen(true)}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)' }
              }}
            >
              Send Receipt
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(`/admin-dashboard/payment-edit/${paymentId || ''}`)}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#3b82f6',
                color: '#3b82f6',
                '&:hover': { borderColor: '#2563eb', bgcolor: 'rgba(59, 130, 246, 0.04)' }
              }}
            >
              Edit
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <Row>
              <Typography variant="body2">Total Payment :</Typography>
              <ValueText>{data.totalPayment}</ValueText>
            </Row>
            <Row>
              <Typography variant="body2">Advance :</Typography>
              <ValueText>{data.advance}</ValueText>
            </Row>
            <Row>
              <Typography variant="body2">Pending :</Typography>
              <ValueText>{data.pending}</ValueText>
            </Row>
            <Row>
              <Typography variant="body2">GST :</Typography>
              <ValueText>{data.gst}</ValueText>
            </Row>
            <Row>
              <Typography variant="body2">Mode :</Typography>
              <ValueText>{data.mode}</ValueText>
            </Row>
            <Row>
              <Typography variant="body2">Sale Credit Month :</Typography>
              <ValueText>{data.saleCreditMonth}</ValueText>
            </Row>
            <Row>
              <Typography variant="body2">Payment Date :</Typography>
              <ValueText>{data.paymentDate}</ValueText>
            </Row>
          </Card>
        </Grid>
      </Grid>

      {/* Send Receipt */}
      <Dialog open={sendOpen} onClose={() => setSendOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Receipt</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField fullWidth label="Recipient Email" margin="dense" defaultValue="client@example.com" />
            <TextField fullWidth label="Subject" margin="dense" defaultValue={`Payment Receipt - ${paymentId || ''}`} />
            <TextField fullWidth label="Message" margin="dense" multiline minRows={4}
              defaultValue={`Please find the attached receipt for payment ${paymentId || ''}.`} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setSendOpen(false)}>Send</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField fullWidth label="Package Cost" margin="dense" defaultValue={data.packageCost} />
            <TextField fullWidth label="Payment Mode" margin="dense" defaultValue={data.paymentMode} />
            <TextField fullWidth label="Mode" margin="dense" defaultValue={data.mode} />
            <TextField fullWidth label="Payment Date" margin="dense" defaultValue={data.paymentDate} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setEditOpen(false)}>Save</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default PaymentProfile;